import { createSelector } from 'reselect'
import API from '../../../utils/http/api'

// Action Types
const REQUEST = 'steppingblocks/adminInvitations/REQUEST'
const SUCCESS = 'steppingblocks/adminInvitations/SUCCESS'
const FAILURE = 'steppingblocks/adminInvitations/FAILURE'
const RESEND_SUCCESS = 'steppingblocks/adminInvitations/RESEND_SUCCESS'
const ADMIN_INVITE_SUCCESS = 'steppingblocks/adminInvitations/ADMIN_INVITE_SUCCESS'
const STUDENT_INVITE_SUCCESS = 'steppingblocks/adminInvitations/STUDENT_INVITE_SUCCESS'
const SET_SEARCH_FILTER = 'steppingblocks/adminInvitations/SET_SEARCH_FILTER'
const TOGGLE_INVITATION_MODAL =
  'steppingblocks/adminInvitations/TOGGLE_INVITATION_MODAL'
const SET_ADMIN_SEARCH_FILTER = 'steppingblocks/adminInvitations/SET_ADMIN_SEARCH_FILTER'
const TOGGLE_ADMIN_INVITATION_MODAL = 'steppingblocks/adminInvitations/TOGGLE_ADMIN_INVITIATION_MODAL'
const RESET_ERROR = 'steppingblocks/adminInvitations/RESET_ADMIN_INVITE_ERROR'

// Initial State
const initialState = {
  requestStarted: false,
  requestEnded: false,
  error: null,
  studentInvitations: [],
  adminInvitations: [],
  searchFilter: '',
  isModalOpen: false,
  adminSearchFilter: '',
  isAdminModalOpen: '',
}

// Reducer
export default (_state = initialState, action) => {
  const { type, payload } = action
  switch (type) {
    case REQUEST:
      return {
        ..._state,
        requestStarted: true,
        requestEnded: false,
        error: null,
      }
    case SUCCESS:
      return {
        ..._state,
        ...payload,
        requestEnded: true,
      }
    case STUDENT_INVITE_SUCCESS:
      return {
        ..._state,
        requestEnded: true,
        studentInvitations: _state.studentInvitations.concat(payload.result)
      }
    case ADMIN_INVITE_SUCCESS:
      return {
        ..._state,
        requestEnded: true,
        adminInvitations: _state.adminInvitations.concat(payload.result),
      }
    case FAILURE:
      return {
        ..._state,
        requestEnded: true,
        error: payload.error,
      }
    case RESEND_SUCCESS:
      return {
        ..._state,
        requestEnded: true,
      }
    case SET_SEARCH_FILTER:
      return {
        ..._state,
        searchFilter: payload.data,
      }
    case SET_ADMIN_SEARCH_FILTER:
      return {
        ..._state,
        adminSearchFilter: payload.data,
      }
    case TOGGLE_INVITATION_MODAL:
      return {
        ..._state,
        isModalOpen: !_state.isModalOpen,
      }
    case TOGGLE_ADMIN_INVITATION_MODAL:
      return {
        ..._state,
        isAdminModalOpen: !_state.isAdminModalOpen,
      }
    case RESET_ERROR:
      return {
        ..._state,
        error: null,
      }
    default:
      return _state
  }
}

// Actions
export const setSearchFilter = data => ({
  type: SET_SEARCH_FILTER,
  payload: { data },
})

export const setAdminSearchFilter = data => ({
  type: SET_ADMIN_SEARCH_FILTER,
  payload: { data },
})

export const toggleModal = () => ({
  type: TOGGLE_INVITATION_MODAL,
})

export const toggleAdminModal = () => ({
  type: TOGGLE_ADMIN_INVITATION_MODAL,
})

// Action Performers
export const performRetrieveInvitations = () => async dispatch => {
  dispatch({ type: REQUEST })
  try {
    const { studentInvitations, adminInvitations } = await API.get(
      '/invitations/admin_invitations',
      {},
    )
    dispatch({
      type: SUCCESS,
      payload: {
        studentInvitations,
        adminInvitations,
      },
    })
  } catch (error) {
    dispatch({
      type: FAILURE,
      payload: { error: error.message },
    })
  }
}

export const performInviteStudent = params => async dispatch => {
  dispatch({ type: REQUEST })
  try {
    const data = await API.post('/invitations/student_invitations', params)
    dispatch({ type: STUDENT_INVITE_SUCCESS, payload: { result: data.invitation } })
    return data
  } catch (error) {
    dispatch({
      type: FAILURE,
      payload: { error: error.message },
    })
    throw new Error(error.message)
  }
}

export const performResendStudentInvite = invitationId => async dispatch => {
  dispatch({ type: REQUEST })
  try {
    await API.get(`/invitations/student_invitations/${invitationId}/resend`)
    dispatch({ type: RESEND_SUCCESS })
    return true
  } catch (error) {
    dispatch({
      type: FAILURE,
      payload: { error: error.message },
    })
    throw new Error(error.message)
  }
}

export const performInviteAdmin = params => async dispatch => {
  dispatch({ type: REQUEST })
  try {
    const data = await API.post('/invitations/admin_invitations', params)
    dispatch({ type: ADMIN_INVITE_SUCCESS, payload: { result: data.invitation } })
    return data
  } catch (error) {
    dispatch({
      type: FAILURE,
      payload: { error: error.message },
    })
    throw new Error(error.message)
  }
}

export const performResendAdminInvite = invitationId => async dispatch => {
  dispatch({ type: REQUEST })
  try {
    await API.get(`/invitations/admin_invitations/${invitationId}/resend`)
    dispatch({ type: RESEND_SUCCESS })
    return true
  } catch (error) {
    dispatch({
      type: FAILURE,
      payload: { error: error.message },
    })
    throw new Error(error.message)
  }
}

export const resetAdminInviteError = () => async dispatch => dispatch({ type: RESET_ERROR })

// Selectors
const adminInvitations = state => state.adminInvitations

export const getStudentInvitations = createSelector(
  adminInvitations,
  _ => _.studentInvitations,
)

export const getAdminInvitations = createSelector(
  adminInvitations,
  _ => _.adminInvitations,
)

export const getSearchFilter = createSelector(
  adminInvitations,
  _ => _.searchFilter,
)

export const getAdminSearchFilter = createSelector(
  adminInvitations,
  _ => _.adminSearchFilter,
)

export const getIsModalOpen = createSelector(
  adminInvitations,
  _ => _.isModalOpen,
)

export const getIsAdminModalOpen = createSelector(
  adminInvitations,
  _ => _.isAdminModalOpen,
)

export const getError = createSelector(adminInvitations, _ => _.error)

export const getFilteredStudentInvitations = createSelector(
  [getStudentInvitations, getSearchFilter],
  (studentInvitations, filter) => studentInvitations.filter(invite => invite.inviteeEmail.toLowerCase().indexOf(filter.toLowerCase()) !== -1)
)

export const getFilteredAdminInvitations = createSelector(
  [getAdminInvitations, getAdminSearchFilter],
  (studentInvitations, filter) => studentInvitations.filter(invite => invite.inviteeEmail.toLowerCase().indexOf(filter.toLowerCase()) !== -1)
)



// WEBPACK FOOTER //
// ./src/routes/authenticated/AdminDashboard/adminInvitationsDuck.js